import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useIsMobile } from '../../utils/useIsMobile';

const POR_PAGINA = 10;

const TIPOS = {
  confirmacion: { label: 'Confirmación', color: '#16a34a', fondo: 'rgba(22,163,74,0.10)',  icono: '✓' },
  recordatorio: { label: 'Recordatorio', color: '#2563eb', fondo: 'rgba(37,99,235,0.10)',  icono: '📅' },
  reagendada:   { label: 'Reagendada',   color: '#7c3aed', fondo: 'rgba(124,58,237,0.10)', icono: '🔄' },
  atendida:     { label: 'Agradecimiento', color: '#0891b2', fondo: 'rgba(8,145,178,0.10)', icono: '🙌' },
  nueva_cita:   { label: 'Aviso al barbero', color: '#c2410c', fondo: 'rgba(194,65,12,0.10)', icono: '💈' },
  otro:         { label: 'Otro',         color: '#6b7280', fondo: 'rgba(107,114,128,0.10)', icono: '•' },
};

// Los tres estados que ve el usuario, derivados de los dos que guarda la base:
// como salio el envio (estado) y que confirmo WhatsApp despues (estado_entrega).
const ENTREGADO = { clave: 'entregado', label: 'Entregado',    color: '#16a34a', fondo: 'rgba(22,163,74,0.12)', icono: '✓' };
const PENDIENTE = { clave: 'pendiente', label: 'Pendiente',    color: '#d97706', fondo: 'rgba(217,119,6,0.12)', icono: '🕐' };
const FALLIDO   = { clave: 'fallido',   label: 'No entregado', color: '#dc2626', fondo: 'rgba(220,38,38,0.12)', icono: '✕' };

function estadoDe(m) {
  if (['fallido', 'invalido', 'omitido'].includes(m.estado)) return FALLIDO;
  if (['entregado', 'leido'].includes(m.estado_entrega)) {
    return m.estado_entrega === 'leido' ? { ...ENTREGADO, label: 'Leído' } : ENTREGADO;
  }
  return PENDIENTE;
}

// creado_en llega en UTC con sufijo "Z": el navegador lo pasa solo a la hora
// local de quien mira.
const fecha = (f) => {
  if (!f) return '';
  const d = new Date(f);
  return isNaN(d) ? f : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const hora = (f) => {
  if (!f) return '';
  const d = new Date(f);
  return isNaN(d) ? '' : d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

// ── Traducción de los errores de los proveedores ────────────────────────────
//
// UltraMsg y Evolution responden en inglés y con su propia jerga. En la base
// se guarda su texto tal cual, que es lo correcto para diagnosticar; aquí se
// convierte en algo accionable, y el original queda disponible por si hace
// falta mirarlo.
const MOTIVOS = [
  { patron: /stopped due to non-payment|extending your subscription/i,
    texto: 'La suscripción de UltraMsg venció y el número quedó desconectado. Se reactiva al renovar el pago.' },
  { patron: /instance not found|instance does not exist|no existe la instancia/i,
    texto: 'La instancia de WhatsApp de este negocio ya no existe. Hay que volver a crearla desde el panel.' },
  { patron: /connection closed|connection lost|not connected|state.*close|logged out/i,
    texto: 'El WhatsApp del negocio está desconectado. Vuelve a vincularlo escaneando el QR.' },
  { patron: /401|unauthorized|invalid.*(api ?key|token)|clave inv/i,
    texto: 'La clave de acceso al servidor de WhatsApp no es válida.' },
  { patron: /exists"?\s*:\s*false|number.*(not|no).*(exist|found)|not a valid whatsapp/i,
    texto: 'Ese número no tiene WhatsApp.' },
  { patron: /timeout|etimedout|econnrefused|enotfound|socket hang up/i,
    texto: 'No se pudo contactar con el servidor de WhatsApp. Puede estar caído o sin conexión.' },
  { patron: /rate ?limit|too many requests|429/i,
    texto: 'Se enviaron demasiados mensajes seguidos y WhatsApp bloqueó el envío por un rato.' },
  { patron: /evolution 5\d\d|internal server error/i,
    texto: 'El servidor de WhatsApp respondió con un error interno.' },
  { patron: /evolution 4\d\d|bad request/i,
    texto: 'El servidor de WhatsApp rechazó el mensaje.' },
];

/**
 * Motivo del fallo en español. Si no reconoce el error, devuelve null y la
 * vista muestra el texto original: mejor algo en inglés que nada.
 */
function motivoLegible(error) {
  if (!error) return null;
  const texto = String(error);
  // Los errores propios ya vienen en español y explicados.
  if (/^[A-ZÁÉÍÓÚÑ]?[a-záéíóúñ ]+ (inválido|invalido)|^El negocio no tiene|^Número inválido/.test(texto)) {
    return texto;
  }
  return MOTIVOS.find(m => m.patron.test(texto))?.texto || null;
}

/**
 * ¿La cita de este mensaje ya pasó? Un mensaje sin cita (por ejemplo el aviso
 * de un gasto) siempre se puede reenviar.
 *
 * El servidor vuelve a comprobarlo antes de enviar; esto solo evita ofrecer un
 * botón que va a fallar.
 */
function citaPasada(m) {
  if (!m?.cita_fecha) return false;
  const fecha = String(m.cita_fecha).slice(0, 10);
  const hora  = String(m.cita_hora || '23:59').slice(0, 5);
  const d = new Date(`${fecha}T${hora}:00`);
  return !isNaN(d) && d.getTime() < Date.now();
}

const hoy      = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const FILTROS_INICIALES = { buscar: '', tipo: '', estado: '', desde: haceDias(30), hasta: hoy() };

export default function NotificationsPage() {
  const esMovil = useIsMobile(1100);

  const [items, setItems]     = useState([]);
  const [resumen, setResumen] = useState(null);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [aviso, setAviso]     = useState('');
  const [sel, setSel]         = useState(null);
  const [reenviando, setReenviando] = useState(false);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const params = { ...filtros, page, limit: POR_PAGINA };
    // El backend traduce "estado" de la vista a sus dos columnas reales.
    if (params.estado === 'entregado')  { params.entrega = 'entregado'; delete params.estado; }
    if (params.estado === 'pendiente')  { params.entrega = 'sin_acuse'; params.estado = 'enviado'; }
    if (params.estado === 'fallido')    { params.estado = 'fallido'; }
    Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
    try {
      const [lista, sum] = await Promise.all([
        api.get('/whatsapp/messages', { params }),
        api.get('/whatsapp/messages/summary', { params }),
      ]);
      setItems(lista.data.items || []);
      setTotal(lista.data.total || 0);
      setResumen(sum.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el historial de notificaciones');
      setItems([]); setResumen(null); setTotal(0);
    } finally { setLoading(false); }
  }, [filtros, page]);

  useEffect(() => { load(); }, [load]);

  // Cualquier cambio de filtro vuelve a la pagina 1: si no, se puede quedar en
  // una pagina que ya no existe y la lista sale vacia sin explicacion.
  const cambiar = (campo, valor) => { setPage(1); setFiltros(f => ({ ...f, [campo]: valor })); };
  const limpiar = () => { setPage(1); setFiltros(FILTROS_INICIALES); };

  const notificar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else         { setAviso(msg); setTimeout(() => setAviso(''), 4000); }
  };

  const reenviar = async (m) => {
    if (citaPasada(m)) {
      return notificar('La cita de este mensaje ya pasó. No se puede reenviar.', true);
    }
    setReenviando(true);
    try {
      await api.post(`/whatsapp/messages/${m.id}/resend`);
      notificar('Notificación reenviada');
      setSel(null);
      load();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo reenviar la notificación', true);
    } finally { setReenviando(false); }
  };

  const copiar = async (m) => {
    try {
      await navigator.clipboard.writeText(m.cuerpo || '');
      notificar('Mensaje copiado');
    } catch {
      notificar('El navegador no permitió copiar', true);
    }
  };

  const paginas = Math.max(Math.ceil(total / POR_PAGINA), 1);
  const desde   = total === 0 ? 0 : (page - 1) * POR_PAGINA + 1;
  const hasta   = Math.min(page * POR_PAGINA, total);
  const pct     = (n) => (resumen?.total ? Math.round((n / resumen.total) * 1000) / 10 : 0);

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">🔔 Notificaciones</h1>
          <p className="page-subtitle">Historial y gestión de notificaciones enviadas por WhatsApp</p>
        </div>
      </div>

      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Tarjetas de resumen */}
      {resumen && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(4, 1fr)',
          gap: '1rem', marginBottom: '1.5rem',
        }}>
          <Tarjeta icono="✓"  color="#16a34a" fondo="rgba(22,163,74,0.12)"  titulo="Entregadas"
                   valor={resumen.entregadas} pie={`${pct(resumen.entregadas)}% del total`} barra={pct(resumen.entregadas)} />
          <Tarjeta icono="🕐" color="#d97706" fondo="rgba(217,119,6,0.12)"  titulo="Pendientes"
                   valor={resumen.pendientes} pie={`${pct(resumen.pendientes)}% del total`} barra={pct(resumen.pendientes)} />
          <Tarjeta icono="✕"  color="#dc2626" fondo="rgba(220,38,38,0.12)"  titulo="No entregadas"
                   valor={resumen.no_entregadas} pie={`${pct(resumen.no_entregadas)}% del total`} barra={pct(resumen.no_entregadas)} />
          <Tarjeta icono="✈"  color="#2563eb" fondo="rgba(37,99,235,0.12)"  titulo="Total enviadas"
                   valor={resumen.total} pie="En el periodo" />
        </div>
      )}

      {/* Filtros */}
      <div className="card" style={{ padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 0 }}>
            <span style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)', fontSize: '0.9rem', pointerEvents: 'none',
            }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 32, margin: 0 }}
              placeholder="Buscar por teléfono, nombre o mensaje..."
              value={filtros.buscar}
              onChange={e => cambiar('buscar', e.target.value)}
            />
          </div>

          <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                 value={filtros.desde} onChange={e => cambiar('desde', e.target.value)} />
          <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                 value={filtros.hasta} onChange={e => cambiar('hasta', e.target.value)} />

          <select className="form-select" style={{ margin: 0, flex: '0 1 170px' }}
                  value={filtros.tipo} onChange={e => cambiar('tipo', e.target.value)}>
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS).map(([v, t]) => <option key={v} value={v}>{t.label}</option>)}
          </select>

          <select className="form-select" style={{ margin: 0, flex: '0 1 170px' }}
                  value={filtros.estado} onChange={e => cambiar('estado', e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="entregado">Entregado</option>
            <option value="pendiente">Pendiente</option>
            <option value="fallido">No entregado</option>
          </select>

          <button className="btn btn-ghost" style={{ flexShrink: 0 }} onClick={limpiar}>↺ Limpiar</button>
        </div>
      </div>

      {/* Listado. El detalle se abre en un modal, no en una columna al lado:
          asi la tabla conserva todo el ancho. */}
      <div>
        <div className="card" style={{ minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No hay notificaciones en el periodo seleccionado
            </div>
          ) : esMovil ? (
            items.map(m => <Tarjetita key={m.id} m={m} activo={sel?.id === m.id} onVer={setSel} />)
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', minWidth: 780 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Fecha y hora</th>
                    <th>Destinatario</th>
                    <th>Tipo</th>
                    <th>Estado entrega</th>
                    <th>Mensaje</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(m => (
                    <Fila key={m.id} m={m} activo={sel?.id === m.id}
                          onVer={setSel} onReenviar={reenviar} reenviando={reenviando} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
            padding: '0.9rem 1.25rem', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
              Mostrando {desde} a {hasta} de {total} resultados
            </span>
            {paginas > 1 && (
              <Paginador page={page} paginas={paginas} onPage={setPage} />
            )}
          </div>
        </div>

      </div>

      {sel && (
        <Modal
          titulo="Detalle de la notificación"
          ancho={460}
          onCerrar={() => setSel(null)}
          pie={<>
            <button className="btn btn-ghost" onClick={() => copiar(sel)}>⧉ Copiar mensaje</button>
            <button className="btn btn-primary" disabled={reenviando || citaPasada(sel)}
                    title={citaPasada(sel) ? 'La cita ya pasó: no se puede reenviar' : undefined}
                    onClick={() => reenviar(sel)}>
              {reenviando ? 'Reenviando...' : '✈ Reenviar notificación'}
            </button>
          </>}
        >
          <Detalle m={sel} />
        </Modal>
      )}
    </div>
  );
}

// ── Piezas ──────────────────────────────────────────────────────────────────

function Tarjeta({ icono, color, fondo, titulo, valor, pie, barra }) {
  return (
    <div className="card" style={{ padding: '1.1rem 1.25rem', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: barra != null ? 10 : 0 }}>
        <div style={{
          width: 42, height: 42, borderRadius: '50%', background: fondo, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.1rem', flexShrink: 0,
        }}>{icono}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{titulo}</div>
          <div style={{ fontSize: '1.65rem', fontWeight: 700, lineHeight: 1.15 }}>{valor}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{pie}</div>
        </div>
      </div>
      {barra != null && (
        <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(barra, 100)}%`, background: color }} />
        </div>
      )}
    </div>
  );
}

/**
 * Motivo del fallo en español, con el texto original del proveedor plegado
 * debajo: quien tenga que reportarlo a soporte lo necesita literal.
 */
function MotivoDelFallo({ error }) {
  const [verOriginal, setVerOriginal] = useState(false);
  const legible = motivoLegible(error);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>⚠ Motivo del fallo</div>
      <div style={{
        background: 'rgba(220,38,38,0.08)', borderRadius: 8, padding: '0.7rem',
        fontSize: '0.8rem', lineHeight: 1.55, wordBreak: 'break-word',
      }}>
        {legible || error}
      </div>

      {legible && (
        <>
          <button
            onClick={() => setVerOriginal(v => !v)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0 0',
              fontSize: '0.72rem', color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)', textDecoration: 'underline',
            }}
          >{verOriginal ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}</button>

          {verOriginal && (
            <div style={{
              marginTop: 4, padding: '0.6rem', borderRadius: 6,
              background: 'var(--color-surface-3)', fontSize: '0.72rem',
              fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-all',
            }}>{error}</div>
          )}
        </>
      )}
    </div>
  );
}

function Punto({ est }) {
  return (
    <div style={{
      width: 24, height: 24, borderRadius: '50%', background: est.fondo, color: est.color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
    }}>{est.icono}</div>
  );
}

function Etiqueta({ tipo }) {
  const t = TIPOS[tipo] || TIPOS.otro;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
      borderRadius: 999, background: t.fondo, color: t.color,
      fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap',
    }}>{t.icono} {t.label}</span>
  );
}

function Fila({ m, activo, onVer, onReenviar, reenviando }) {
  const est = estadoDe(m);
  return (
    <tr onClick={() => onVer(m)}
        style={{ cursor: 'pointer', background: activo ? 'rgba(79,70,229,0.08)' : undefined }}>
      <td><Punto est={est} /></td>
      <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
        <div style={{ fontWeight: 600 }}>{fecha(m.creado_en)}</div>
        <div style={{ color: 'var(--color-text-muted)' }}>{hora(m.creado_en)}</div>
      </td>
      <td style={{ fontSize: '0.83rem', minWidth: 0 }}>
        {m.nombre_destinatario && <div style={{ fontWeight: 600 }}>{m.nombre_destinatario}</div>}
        <div style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
          {m.destinatario === 'barbero' ? '💈 ' : ''}{m.telefono || '—'}
        </div>
      </td>
      <td><Etiqueta tipo={m.tipo} /></td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span style={{
          display: 'inline-block', padding: '3px 9px', borderRadius: 999,
          background: est.fondo, color: est.color, fontSize: '0.72rem', fontWeight: 600,
        }}>{est.label}</span>
        {m.actualizado_en && est.clave === 'entregado' && (
          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
            {hora(m.actualizado_en)}
          </div>
        )}
      </td>
      <td style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', maxWidth: 260 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(m.cuerpo || '').replace(/[*_\n]/g, ' ').trim() || '—'}
        </div>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button className="btn btn-ghost" title="Ver detalle"
                style={{ padding: '3px 8px', fontSize: '0.8rem' }}
                onClick={e => { e.stopPropagation(); onVer(m); }}>👁</button>
        <button className="btn btn-ghost" disabled={reenviando || citaPasada(m)}
                title={citaPasada(m) ? 'La cita ya pasó: no se puede reenviar' : 'Reenviar notificación'}
                style={{ padding: '3px 8px', fontSize: '0.8rem', marginLeft: 4 }}
                onClick={e => { e.stopPropagation(); onReenviar(m); }}>✈</button>
      </td>
    </tr>
  );
}

function Tarjetita({ m, activo, onVer }) {
  const est = estadoDe(m);
  return (
    <div onClick={() => onVer(m)} style={{
      padding: '0.9rem 1.1rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
      background: activo ? 'rgba(79,70,229,0.08)' : undefined,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <Punto est={est} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
          <strong style={{ fontSize: '0.86rem' }}>{m.nombre_destinatario || m.telefono || '—'}</strong>
          <span style={{ fontSize: '0.72rem', color: est.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{est.label}</span>
        </div>
        <div style={{ marginBottom: 5 }}><Etiqueta tipo={m.tipo} /></div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
          {fecha(m.creado_en)} · {hora(m.creado_en)}
        </div>
      </div>
    </div>
  );
}

/** Contenido del detalle. La cabecera, el pie y los botones los pone el Modal. */
function Detalle({ m }) {
  const est = estadoDe(m);

  const dato = (icono, etiqueta, valor) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 2 }}>
        {icono} {etiqueta}
      </div>
      <div style={{ fontSize: '0.86rem', fontWeight: 500, wordBreak: 'break-word' }}>{valor || '—'}</div>
    </div>
  );

  const citaTexto = m.cita_id
    ? `#${m.cita_id}${m.cita_fecha ? ` (${String(m.cita_fecha).slice(0, 10)}${m.cita_hora ? ` · ${String(m.cita_hora).slice(0, 5)}` : ''})` : ''}`
    : null;

  return (
    <div style={{ minWidth: 0 }}>
        <div style={{
          background: est.fondo, borderRadius: 10, padding: '0.9rem 1rem', marginBottom: '1.25rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: est.color, fontWeight: 700 }}>
            <Punto est={est} /> {est.label}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
            Enviado: {fecha(m.creado_en)} {hora(m.creado_en)}
            {m.actualizado_en && <><br />Actualizado: {fecha(m.actualizado_en)} {hora(m.actualizado_en)}</>}
          </div>
        </div>

        {dato('👤', 'Destinatario', m.nombre_destinatario || (m.destinatario === 'barbero' ? 'Barbero' : 'Cliente'))}
        {dato('📞', 'Teléfono', m.telefono)}
        {m.business_name && dato('🏪', 'Negocio', m.business_name)}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>🏷 Tipo</div>
          <Etiqueta tipo={m.tipo} />
        </div>
        {citaTexto && dato('📅', 'Cita', (
          <>
            {citaTexto}
            {citaPasada(m) && (
              <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                Ya pasó · no se puede reenviar
              </div>
            )}
          </>
        ))}
        {dato('📡', 'Proveedor WhatsApp', m.proveedor)}
        {m.message_id && dato('🆔', 'Message ID', <span style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{m.message_id}</span>)}
        {m.reenvio_de && dato('↻', 'Reenvío de', `Notificación #${m.reenvio_de}`)}

        {m.error && <MotivoDelFallo error={m.error} />}

        <div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>💬 Mensaje</div>
          <div style={{
            background: 'var(--color-surface-3)', borderRadius: 8, padding: '0.85rem',
            fontSize: '0.82rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 220, overflowY: 'auto',
          }}>{m.cuerpo || '—'}</div>
        </div>
    </div>
  );
}

function Paginador({ page, paginas, onPage }) {
  // Se muestran como mucho 5 numeros alrededor de la pagina actual, mas los
  // extremos, para que la barra no crezca sin control con muchas paginas.
  const nums = [];
  const desde = Math.max(1, Math.min(page - 2, paginas - 4));
  const hasta = Math.min(paginas, desde + 4);
  for (let i = desde; i <= hasta; i++) nums.push(i);

  const btn = (label, destino, activo = false, deshabilitado = false) => (
    <button key={label + destino}
            className={`btn ${activo ? 'btn-primary' : 'btn-ghost'}`}
            disabled={deshabilitado}
            style={{ padding: '3px 10px', fontSize: '0.8rem', minWidth: 34 }}
            onClick={() => onPage(destino)}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {btn('‹', page - 1, false, page <= 1)}
      {desde > 1 && <>{btn('1', 1)}<span style={{ color: 'var(--color-text-muted)' }}>…</span></>}
      {nums.map(n => btn(String(n), n, n === page))}
      {hasta < paginas && <><span style={{ color: 'var(--color-text-muted)' }}>…</span>{btn(String(paginas), paginas)}</>}
      {btn('›', page + 1, false, page >= paginas)}
    </div>
  );
}
